import "./index.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMAGE_UPLOAD, YOUTUBE_URL } from "../../../../Constants/types";
import { getFilePath, uploadFile } from "../../../../Utils/utils";
import UploadPanel from "../../../../components/UploadPanel";
import Firebase from "../../../../firebasehelper";
import SingleFileUpload from "../SingleFileUpload";
import ReactSelect from "react-select";
import { portfolioCategories } from "./constants";
import { comparePortfolios, selectStyles } from "./utils";
import UploadSectionHeader from "../UploadSectionHeader";
import { colors } from "../../../../enums";

export default function PortfolioModal({
  brandId,
  brandData,
  selectedPortfolio,
  setSelectedPortfolio,
}) {
  const modalRef = useRef();
  const modalContentRef = useRef();

  const [shouldReset, setShouldReset] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [userInput, setUserInput] = useState({
    name: "",
    media: [{ panelName: "", uploads: [] }],
    // mediaToDelete is used as a placeholder as UploadPanel expects it but has no use in this modal
    mediaToDelete: [],
    contents: Array.from({ length: 8 }, (_, id) => ({ id, text: "" })),
    introduction: "",
    portfolioLogo: "",
    categories: [],
    shouldResetYoutubeInput: false,
  });

  const hasCustomers = userInput.categories.some(
    (category) => category.label === "Customers"
  );

  const tagOptions = Object.values(colors).map((color) => ({
    label: `${color[0].toUpperCase()}${color.slice(1)}`,
    value: color,
  }));

  useEffect(() => {
    const onOutsideClick = async (e) => {
      const isOpen = modalRef.current.classList.contains("show");
      if (isOpen && !modalContentRef.current.contains(e.target)) {
        setShouldReset(true);
      }
    };

    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  useEffect(() => {
    if (shouldReset) {
      onClose();
      setShouldReset(false);
    }
  }, [shouldReset]);

  useEffect(() => {
    if (selectedPortfolio) {
      const { portfolioLogo, ...portfolio } = selectedPortfolio;

      setUserInput((prevUserInput) => ({
        ...prevUserInput,
        ...portfolio,
      }));
      setLogoPreview(portfolioLogo);
    }
  }, [selectedPortfolio]);

  const onUploadPanelInput = (idx, updatedObj) => {
    const { media: uploads } = updatedObj;
    const updatedMedia = userInput.media.map((panel, i) => {
      if (i === idx) {
        return {
          ...panel,
          uploads: uploads,
        };
      }
      return panel;
    });

    setUserInput((prevState) => ({
      ...prevState,
      media: updatedMedia,
    }));
  };

  const onInputChange = (name, value, id) => {
    let inputValue;
    if (name === "contents") {
      inputValue = userInput.contents.map((content) =>
        content.id === id ? { ...content, text: value } : content
      );
    } else if (name === "categories") {
      inputValue = value || [];
    } else {
      inputValue = value;
    }

    setUserInput((prevUserInput) => ({
      ...prevUserInput,
      [name]: inputValue,
    }));
  };

  const resets = () => {
    setUserInput({
      name: "",
      media: [{ panelName: "", uploads: [] }],
      // mediaToDelete is used as a placeholder as UploadPanel expects it but has no use in this modal
      mediaToDelete: [],
      contents: Array.from({ length: 8 }, (_, id) => ({ id, text: "" })),
      introduction: "",
      portfolioLogo: "",
      categories: [],
      shouldResetYoutubeInput: false,
    });

    setLogoFile(null);
    setLogoPreview("");
    selectedPortfolio && setSelectedPortfolio(null);
  };

  const deleteUploadedMedia = async (mediaArray) => {
    if (!mediaArray || !mediaArray.length) return;

    const deletePromises = mediaArray
      .filter((item) => item && item.mediaType !== YOUTUBE_URL)
      .map((item) => {
        const path = getFilePath("portfolios", item.mediaUrl, item.mediaType);
        return Firebase.deleteFile(path);
      });

    await Promise.all(deletePromises);
  };

  // TODO: Potential future work - Instead of uploading files on selection, display a preview
  // and only upload files on save
  const removeUnsavedFilesFromStorage = async (media) => {
    const newlyAddedMedia = comparePortfolios(selectedPortfolio.media, media);

    if (newlyAddedMedia.length === 0) return;

    const deletePromises = newlyAddedMedia
      .filter((item) => item && item.mediaType !== YOUTUBE_URL)
      .map(async (item) => {
        const path = getFilePath("portfolios", item.mediaUrl, item.mediaType);
        try {
          await Firebase.deleteFile(path);
        } catch (e) {
          console.error("Error deleting file:", e);
        }
      });

    await Promise.all(deletePromises);
  };

  const onClose = async () => {
    const isAddPortfolioModal = !selectedPortfolio;

    if (isAddPortfolioModal) {
      const mediaToDelete = userInput.media.flatMap((panel) => panel.uploads);
      await deleteUploadedMedia(mediaToDelete);
    } else await removeUnsavedFilesFromStorage(userInput.media);

    // timeout for better UX
    setTimeout(() => {
      resets();
    }, 300);
  };

  const isValidInput = useMemo(() => {
    let isValidPanel = userInput.media.every((panel) =>
      panel.uploads.some((item) => item)
    );

    if (hasCustomers) {
      isValidPanel =
        isValidPanel &&
        userInput.media.every((panel) => panel.panelName.length > 0);
    }

    const hasName = userInput.name.length > 0;
    const hasAtleastOneBulletPoint = userInput.contents[0].text.length > 0;
    const hasIntroduction = userInput.introduction.length > 2;

    return (
      hasName && isValidPanel && hasAtleastOneBulletPoint && hasIntroduction
    );
  }, [userInput]);

  const onDeleteLogo = async (logoPath) => {
    const isAddPortfolioModal = !selectedPortfolio;
    const isLocalPreviewInEditModal =
      selectedPortfolio && !selectedPortfolio.portfolioLogo;

    if (isAddPortfolioModal || isLocalPreviewInEditModal) {
      setLogoFile(null);
      setLogoPreview("");
      return;
    }

    // else we have to deal with a logo that has been uploaded to storage
    const arrayWithLogoObj = [{ mediaUrl: logoPath, mediaType: IMAGE_UPLOAD }];
    const updatedPortfolioData = {
      portfolioLogo: "",
      id: selectedPortfolio.id,
    };

    try {
      await deleteUploadedMedia(arrayWithLogoObj);
      await Firebase.updateBrandPortfolioById(brandId, updatedPortfolioData);

      setLogoFile(null);
      setLogoPreview("");
    } catch (e) {
      console.error("Delete logo error", e);
    }
  };

  const savePortfolio = async () => {
    const hasPortfolioLogo =
      selectedPortfolio && selectedPortfolio.portfolioLogo;

    let portfolioData = {
      name: userInput.name,
      media: userInput.media,
      contents: userInput.contents,
      introduction: userInput.introduction,
      categories: userInput.categories,
      portfolioLogo: hasPortfolioLogo ? selectedPortfolio.portfolioLogo : "",
      isPublic: selectedPortfolio ? selectedPortfolio.isPublic : true,
      id: selectedPortfolio ? selectedPortfolio.id : null,
    };

    try {
      if (logoFile) {
        const path = `portfolios/${brandId}/images`;
        const logoPath = await uploadFile(logoFile, path);
        if (logoPath) portfolioData.portfolioLogo = logoPath;
      }

      if (selectedPortfolio) {
        await Firebase.updateBrandPortfolioById(brandId, portfolioData);
      } else await Firebase.addBrandPortfolioById(brandId, portfolioData);
      resets();
    } catch (e) {
      console.error("Error saving portfolio", e);
    }
  };

  const addPanel = () => {
    setUserInput((prevState) => ({
      ...prevState,
      media: [...prevState.media, { panelName: "", uploads: [] }],
    }));
  };

  const removePanel = async (panelIndex) => {
    setUserInput((prevState) => ({
      ...prevState,
      media: prevState.media.filter((_panel, i) => i !== panelIndex),
    }));
  };

  const onPanelNameChange = (e, i) => {
    const updatedMedia = userInput.media.map((panel, j) => {
      if (i === j) return { ...panel, panelName: e.target.value };
      return panel;
    });

    setUserInput((prevState) => ({
      ...prevState,
      media: updatedMedia,
    }));
  };

  const onTagChange = (panelIdx, uploadIdx, selectedOption) => {
    const updatedMedia = userInput.media.map((panel, i) => {
      if (i !== panelIdx) return panel;

      const updatedUploads = panel.uploads.map((upload, j) => {
        if (j !== uploadIdx) return upload;

        return {
          ...upload,
          tag: selectedOption.value,
        };
      });

      return {
        ...panel,
        uploads: updatedUploads,
      };
    });

    setUserInput((prevState) => ({
      ...prevState,
      media: updatedMedia,
    }));
  };

  const shouldRenderMoreButton = (i) => {
    return (
      hasCustomers &&
      userInput.media.length < 4 &&
      i === userInput.media.length - 1
    );
  };

  return (
    <div
      className="modal fade"
      id="portfolio-modal"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="modal-default-fadein"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content" ref={modalContentRef}>
          <div className="modal-header">
            <h3 className="modal-title">
              {selectedPortfolio ? "Edit portfolio" : "Create portfolio"}
            </h3>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <label className="portfolio-modal__label">Portfolio name</label>
            <input
              type="text"
              className="form-control form-control form-control-alt"
              name="name"
              onChange={(e) => onInputChange("name", e.target.value)}
              placeholder="Type portfolio name"
              value={userInput.name}
            />

            <label className="portfolio-modal__label">
              Portfolio categories
            </label>
            <ReactSelect
              placeholder="Select categories"
              className="portfolio__select"
              isMulti
              value={userInput.categories}
              options={portfolioCategories}
              styles={selectStyles(brandData.hex, brandData.white)}
              onChange={(value) => onInputChange("categories", value)}
            />

            <SingleFileUpload
              label="Portfolio logo"
              logoPreview={logoPreview}
              setLogoPreview={setLogoPreview}
              hex={brandData.hex}
              white={brandData.white}
              updateState={setLogoFile}
              onDelete={onDeleteLogo}
            />

            <h4 className="portfolio-modal__label">Portfolio content</h4>
            {userInput.media.map((panel, i) => (
              <article key={i} className="portfolio-modal__uploads">
                {hasCustomers && (
                  <UploadSectionHeader
                    id={i}
                    value={panel.panelName}
                    onNameChange={onPanelNameChange}
                    shouldRenderButton={userInput.media.length > 1}
                    removeUploadPanel={removePanel}
                  />
                )}
                <UploadPanel
                  media={panel.uploads}
                  updateState={(updatedObj) =>
                    onUploadPanelInput(i, updatedObj)
                  }
                  brandId={brandId}
                  hex={brandData.hex}
                  white={brandData.white}
                  mediaToDelete={userInput.mediaToDelete}
                  shouldResetYoutubeInput={userInput.shouldResetYoutubeInput}
                  isEditModal={selectedPortfolio ? true : false}
                  uploadDirectory="portfolios"
                  uploadLimit={6}
                  tagOptions={hasCustomers ? tagOptions : null}
                  onTagChange={(uploadIdx, value) =>
                    onTagChange(i, uploadIdx, value)
                  }
                />
                {shouldRenderMoreButton(i) && (
                  <button
                    className="btn portfolio-modal__increment-btn"
                    onClick={addPanel}
                    style={{
                      "--hex": brandData.hex || "white",
                      "--text-color": brandData.white ? "white" : "black",
                    }}
                  >
                    Add a sub-section of content
                  </button>
                )}
              </article>
            ))}

            <label className="portfolio-modal__label">
              Portfolio introduction
            </label>
            <textarea
              className="form-control form-control form-control-alt"
              name="introduction"
              value={userInput.introduction}
              placeholder="Keep it short and snappy."
              onChange={(e) => onInputChange("introduction", e.target.value)}
            />

            <label className="portfolio-modal__label">Portfolio features</label>
            <div className="portfolio-modal__features">
              {userInput.contents.map((contentItem) => {
                return (
                  <input
                    type="text"
                    className="form-control form-control form-control-alt content-input"
                    id={contentItem.id}
                    onChange={(e) =>
                      onInputChange("contents", e.target.value, contentItem.id)
                    }
                    value={contentItem.text}
                    key={contentItem.id}
                  />
                );
              })}
            </div>
          </div>
          <div
            className={`modal-footer ${
              selectedPortfolio ? "modal-footer--space-between" : ""
            }`}
          >
            <button
              type="button"
              className="btn btn-delete"
              style={{ display: selectedPortfolio ? "block" : "none" }}
              data-dismiss="modal"
              data-toggle="modal"
              data-target="#delete-portfolio"
            >
              Delete
            </button>
            <div
              className={`modal-footer__container ${
                selectedPortfolio ? "" : "modal-footer__add-portfolio"
              }`}
            >
              <button
                type="button"
                className="btn btn-brand-hex btn-white"
                style={{
                  "--hover-text-color": brandData.white ? "white" : "black",
                  "--hover-color": brandData.hex,
                  backgroundColor: "white",
                  color: "black",
                }}
                data-dismiss="modal"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                data-dismiss="modal"
                className={`btn btn-green ${
                  isValidInput ? "" : "popup__btn--invalid"
                }`}
                disabled={!isValidInput}
                onClick={savePortfolio}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/screens/ProductPortfolio/components/PortfolioModal/index.js